import Vue from 'vue'
import WorkService from './work_service'
import DateService from './date_service'
import JsPDF from 'jspdf'
import 'jspdf-autotable'
import SegoeUIFont from '../assets/segoeui_font'
import moment from 'moment'

export default class PrintService {
  /**
   * // Retourne le PDF contenant les détails de tous les travaux d'une classe dans la plage de date
   * @param {array} students
   * @param {string} fromDate
   * @param {string} toDate
   * @param {string} chronological
   * @param {string} comments
   * @retun {JsPDF} doc
   */
  static async getClassroomDetailsPdf (students, fromDate, toDate, chronological, comments) {
    let doc = new JsPDF('p', 'pt')
    // Parcourir students et call getWorksDetailsPdf pour chaque élève
    let emptyClassroomDetails = 0
    let systemHasWrittenSomething = false
    for (let student of students.entries()) {
      // Récupère les travaux de l'élève dans l'itération
      const worksResponse = await WorkService.getWorks(student[1].login.toLowerCase())
      if (!worksResponse) {
        Vue.prototype.$message.error('Une erreur inconnue est survenue, veuillez réessayer')
        return
      }
      // Si l'élève dans l'itération actuelle n'a pas de travaux on passe au suivant
      let worksData = worksResponse.data.data
      if (!Array.isArray(worksData) || worksData.length <= 0) {
        emptyClassroomDetails++
        continue
      }
      // Conversion des dates
      worksData = DateService.formatObjectArrayDatesToDisplayFormat(worksData, 'display')
      // api : pour chaque travail, récup détails dans plage de dates
      let emptyWorks = 0
      let emptyStudentDetails = 0
      for (let [index, work] of worksData.entries()) {
        // Récupère les détails du travail dans l'itération
        const detailsResponse = await WorkService.getDetailsInRange(work.numero, fromDate, toDate, chronological)
        let detailsData
        if (detailsResponse) {
          detailsData = detailsResponse.data.data
          // Teste l'exstence de détails
          if (!Array.isArray(detailsData) || detailsData.length <= 0) {
            emptyWorks++
            emptyClassroomDetails++
            emptyStudentDetails++
            continue
          }
        }
        detailsData = DateService.formatObjectArrayDatesToDisplayFormat(detailsData, 'display')
        // Évite une première page vide
        if (index !== 0 && emptyWorks !== index) {
          doc.addPage()
        }
        this.getDetails(doc, work, detailsData, comments)
        systemHasWrittenSomething = true
      }
      // Rajoute une page pour le prochain travail
      // Ne pas rajouter la page si l'utilisateur n'a que des travaux vide
      if (emptyStudentDetails !== worksData.length) {
        emptyStudentDetails = false
        doc.addPage()
      }
    }
    if (emptyClassroomDetails === students.length || !systemHasWrittenSomething) {
      Vue.prototype.$message.error('Aucun détail à imprimer')
      return
    }
    // Suppression de la page vide
    let pageCount = doc.internal.getNumberOfPages()
    doc.deletePage(pageCount)
    return doc
  }
  /**
   * Retourne le PDF contenant les détails du travail spécifié dans la plage de date
   * @param {integer} numero
   * @param {string} fromDate
   * @param {string} toDate
   * @param {string} chronological
   * @param {string} comments
   * @retun {JsPDF} doc
   */
  static async getDetailsPdf (numero, fromDate, toDate, chronological, comments) {
    // api : récupérer le travail
    const workResponse = await WorkService.getWork(numero)
    if (!workResponse) {
      Vue.prototype.$message.error('Une erreur inconnue est survenue, veuillez réessayer')
      return
    }
    let workData = workResponse.data.data
    workData = DateService.formatObjectArrayDatesToDisplayFormat(workData, 'display')[0]
    // api : récupérer les détails
    const detailsResponse = await WorkService.getDetailsInRange(numero, fromDate, toDate, chronological)
    let detailsData
    if (detailsResponse) {
      detailsData = detailsResponse.data.data
      // Teste l'exstence de détails
      if (!Array.isArray(detailsData) || detailsData.length <= 0) {
        Vue.prototype.$message.error('Aucun détail à imprimer')
        return
      }
    }
    detailsData = DateService.formatObjectArrayDatesToDisplayFormat(detailsData, 'display')
    let doc = new JsPDF('p', 'pt')
    this.getDetails(doc, workData, detailsData, comments)
    return doc
  }
  /**
   * Retourne le PDF contenant tous les détails sur plusieurs travaux dans une plage de dates
   * @param {string} login
   * @param {string} fromDate
   * @param {string} toDate
   * @param {string} chronological
   * @param {string} comments
   */
  static async getWorksDetailsPdf (login, fromDate, toDate, chronological, comments) {
    let doc = new JsPDF('p', 'pt')
    // api : récup tous les travaux de l'utilisateur fourni
    const worksResponse = await WorkService.getWorks(login)
    if (!worksResponse) {
      Vue.prototype.$message.error('Une erreur inconnue est survenue, veuillez réessayer')
      return
    }
    let worksData = worksResponse.data.data
    if (!Array.isArray(worksData) || worksData.length <= 0) {
      Vue.prototype.$message.error('Aucun travail à imprimer')
      return
    }
    worksData = DateService.formatObjectArrayDatesToDisplayFormat(worksData, 'display')
    // api : pour chaque travail, récup détails dans plage de dates
    let emptyWorks = 0
    for (let [index, work] of worksData.entries()) {
      const detailsResponse = await WorkService.getDetailsInRange(work.numero, fromDate, toDate, chronological)
      let detailsData
      if (detailsResponse) {
        detailsData = detailsResponse.data.data
        // Teste l'exstence de détails
        if (!Array.isArray(detailsData) || detailsData.length <= 0) {
          emptyWorks++
          continue
        }
      }
      detailsData = DateService.formatObjectArrayDatesToDisplayFormat(detailsData, 'display')
      // Évite une première page vide
      if (index !== 0 && emptyWorks !== index) {
        doc.addPage()
      }
      this.getDetails(doc, work, detailsData, comments)
    }
    if (emptyWorks === worksData.length) {
      Vue.prototype.$message.error('Aucun détail à imprimer')
      return
    }
    return doc
  }
  /**
   * Génère un tableau des détails dans un fichier pdf
   * @param {jspdf} doc
   * @param {array} workData
   * @param {array} detailsData
   * @param {bool} comments
   * @returns {object} jsPDF
   */
  static getDetails (doc, workData, detailsData, comments) {
    let columns = [{ status: ' ', date: 'Date', periods: 'Période(s)' }]
    // Inclusion des remarques
    if (comments) {
      Object.assign(columns[0], { comment: 'Remarque' })
    }
    // Préparation des données
    let transformedData = []
    detailsData.forEach((element) => {
      // Rien et option indifférent
      if (!element.loginValidateur && !element.loginCorrecteur) {
        transformedData.push({
          status: { content: '' },
          date: { content: element.dateDetail, styles: { valign: 'middle' } },
          periods: { content: element.nbPeriodes, styles: { valign: 'middle', halign: 'center' } },
          comment: { content: '-' }
        })
      // Remarque et option ON
      } else if (!element.loginValidateur && element.loginCorrecteur && comments) {
        transformedData.push({
          status: { content: '⚠', styles: { valign: 'middle' } },
          date: { content: element.dateDetail, styles: { valign: 'middle' } },
          periods: { content: element.nbPeriodes, styles: { valign: 'middle', halign: 'center' } },
          comment: { content: `${element.remarque} ~ ${element.loginCorrecteur} le ${element.dateRemediation} à ${element.heureRemediation}` }
        })
      // Remarque et option OFF
      } else if (!element.loginValidateur && element.loginCorrecteur && !comments) {
        transformedData.push({
          status: { content: '⚠', styles: { valign: 'middle' } },
          date: { content: element.dateDetail, styles: { valign: 'middle' } },
          periods: { content: element.nbPeriodes, styles: { valign: 'middle', halign: 'center' } },
          comment: { content: '-' }
        })
      // Validé + remarque et option ON
      } else if (element.loginValidateur && element.loginCorrecteur && comments) {
        transformedData.push({
          status: { content: '✓', styles: { valign: 'middle' } },
          date: { content: element.dateDetail, styles: { valign: 'middle' } },
          periods: { content: element.nbPeriodes, styles: { valign: 'middle', halign: 'center' } },
          comment: { content: `Validé par ${element.loginValidateur} le ${element.dateValidation} à ${element.heureValidation}\n\nCe détail avait fait l'objet d'une remarque par ${element.loginCorrecteur} le ${element.dateRemediation} à ${element.heureRemediation} :\n\n${element.remarque}` }
        })
      // Validé et option indifférent
      } else if (element.loginValidateur) {
        transformedData.push({
          status: { content: '✓', styles: { valign: 'middle' } },
          date: { content: element.dateDetail, styles: { valign: 'middle' } },
          periods: { content: element.nbPeriodes, styles: { valign: 'middle', halign: 'center' } },
          comment: { content: `Validé par ${element.loginValidateur} le ${element.dateValidation} à ${element.heureValidation}` }
        })
      }
      transformedData.push({ status: { content: element.detail, colSpan: 4 } })
    })
    // Préparation du pdf
    // Custom font
    doc.addFileToVFS('seguisym.ttf', SegoeUIFont.font)
    doc.addFont('seguisym.ttf', 'seguisym', 'normal')
    doc.addFont('seguisym.ttf', 'seguisym', 'bold')
    doc.setFont('seguisym', 'normal')
    doc.setFontSize(22)
    doc.setTextColor(0, 0, 0)
    let workTitle = doc.splitTextToSize(`Travail : ${workData.titreTravail}`, 475)
    doc.text(workTitle, 70, 70)
    doc.setFontSize(12)
    doc.text(`Total de ${this.totalWorkPeriods(detailsData)} période(s) créé le ${workData.dateCreation} par ${workData.loginCreateur} (${workData.classeCreateur})`, 70, 90)
    // Génération du pdf
    doc.autoTable({
      startY: 110,
      margin: { left: 70, bottom: 80 },
      showHead: 'firstPage',
      rowPageBreak: 'avoid',
      styles: {
        fontSize: 11,
        font: 'seguisym'
      },
      headStyles: {
        fontSize: 11,
        font: 'seguisym'
      },
      columnStyles: {
        status: { cellWidth: 2 },
        date: { cellWidth: 40 },
        periods: { cellWidth: 10 },
        comment: { cellWidth: 160 }
      },
      didParseCell: (data) => {
        if (data.column.dataKey === 'periods' && data.section === 'head') {
          // Centrage de l'header Période(s)
          data.cell.styles.halign = 'center'
        } else if (data.cell.colSpan === 4) {
          data.cell.styles.cellPadding = { left: 28, bottom: 30 }
        } else if (data.cell.colSpan === 1 && data.section === 'body') {
          data.cell.styles.fillColor = [235, 235, 235]
        }
      },
      didDrawPage: (data) => {
        this.appendFooter(doc)
      },
      head: columns,
      body: transformedData
    })
    // Affichage du commentaire s'il y en a
    if (workData.remarque) {
      // Affichage sur nouvelle page si pas assez de place
      if (doc.autoTable.previous.finalY + 10 > 450) {
        doc.addPage()
        this.appendGlobalComment(doc, 70, 100, 125, workData)
        // Footer
        this.appendFooter(doc)
      } else {
        this.appendGlobalComment(doc, doc.autoTable.previous.finalY + 10, doc.autoTable.previous.finalY + 30, doc.autoTable.previous.finalY + 55, workData)
      }
    }
    return doc
  }
  static totalWorkPeriods (detailsData) {
    return Array.isArray(detailsData) && detailsData.length ? detailsData.map(item => item.nbPeriodes).reduce((prev, next) => prev + next) : 0
  }
  static appendFooter (doc) {
    doc.setFontSize(10)
    doc.text(`-${doc.internal.getNumberOfPages()}-`, 295, 770)
    doc.setFontSize(9)
    doc.setTextColor(200, 200, 200)
    doc.text(`document généré le ${moment().format(DateService.dateDisplayFormat)}`, 425, 770)
  }
  static appendGlobalComment (doc, y1, y2, y3, workData) {
    doc.line(70, y1, 550, y1)
    doc.setFontSize(14)
    doc.text('Commentaire global du travail : ', 70, y2)
    doc.setFontSize(12)
    doc.setTextColor(95, 95, 95)
    let globalComment = doc.splitTextToSize(workData.remarque, 475)
    doc.text(globalComment, 70, y3)
  }
}
